import {
  MediaType,
  type SchaefflerImageDTO,
  type SchaefflerMediaDTO,
  type SchaefflerVideoDTO,
} from "~/features/cms/types/dto";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { cloneElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { getNextUID } from "~/common/utils/uid";
import { addVideo, removeVideo } from "~/utils/integrations/movingImages";

export const CMSMedia = (props: SchaefflerMediaDTO & { className?: string; disabled?: boolean }) => {
  let mediaItem;
  if (props.type === MediaType.image) {
    mediaItem = <CMSImage {...props} className={undefined} />;
  } else {
    mediaItem = <CMSVideo {...props} className={undefined} />;
  }

  const ratio = props.ratio;
  const style = useMemo(() => {
    if (!ratio) {
      return undefined;
    }
    return { aspectRatio: ratio };
  }, [ratio]);

  if (style) {
    return (
      <div className={classNames(props.className, "scha__forced-ratio-image")} style={style}>
        {mediaItem}
      </div>
    );
  } else {
    return cloneElement(mediaItem, {
      className: classNames("w-100 scha__forced-ratio-image-with-border-radius", props.className),
    });
  }
};

const CMSVideo = (props: Omit<SchaefflerVideoDTO, "type"> & { className?: string; disabled?: boolean }) => {
  const [id] = useState(() => "vid-" + getNextUID());

  const videoActive = useRef(false);

  useEffect(() => {
    if (!props.videoUrl) {
      return;
    }
    if (!props.disabled) {
      addVideo(id, props.videoUrl);
      videoActive.current = true;
    } else if (props.disabled && videoActive.current) {
      removeVideo(id);
      videoActive.current = false;
    }

    return () => {
      if (videoActive.current) {
        removeVideo(id);
      }
    };
  }, [props.videoUrl, props.disabled, id]);

  if (props.disabled) {
    const image = props.disabledImage ?? "https://cdn.schaeffler-ecommerce.com/frontend/gated-content.jpg";
    return <CMSImage id={props.id} ratio={props.ratio} imageUrl={image} alt={props.caption} />;
  }

  return <div id={id} className={props.className} />;
};

const CMSImage = (props: Omit<SchaefflerImageDTO, "type"> & { className?: string }) => {
  const isDesktop = useIsMinDesktopSize();
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgCls, setImgCls] = useState("");

  const imgUrl = pickImage(props, isDesktop);

  const ratio = props.ratio;
  useLayoutEffect(() => {
    if (imgRef.current && ratio) {
      const { width, height } = imgRef.current.getBoundingClientRect();
      setImgCls(width > height ? "scha__forced-ratio-image--h" : "scha__forced-ratio-image--w");
    }
  }, [ratio, imgUrl]);

  return (
    <img className={classNames(props.className, imgCls)} src={imgUrl} ref={imgRef} alt={props.alt ?? props.caption} />
  );
};

export const pickImage = (obj: Pick<SchaefflerImageDTO, "mobileImageUrl" | "imageUrl">, isDesktop: boolean) => {
  const images = [obj.mobileImageUrl, obj.imageUrl];
  let image: string | undefined;
  const index = isDesktop ? 1 : 0;
  image = images[index];
  if (!image) {
    image = images.filter((url) => !!url)[0];
  }
  return image;
};
